import { useState, useEffect, useMemo, useRef, useCallback, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DollarSign, Loader2, AlertCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import AttachmentsSection from '../components/AttachmentsSection';
import { getSuppliers, getSupplierById } from '../utils/supplierApi';
import { getVendorPayableBills } from '../utils/billApi';
import {
  applyBillPaymentAllocations,
  createBillPayment,
  getBillPayments,
} from '../utils/billPaymentApi';
import { listStagedAttachments, syncStagedAttachmentNotes } from '../utils/attachmentApi';
import {
  BILL_PAYMENT_UPLOAD_SESSION_KEY,
  getOrCreateUploadSessionId,
} from '../utils/uploadSession';
import { ApiValidationError } from '../types/auth';
import type { Supplier } from '../types/supplier';
import type { BillPayment, BillPaymentMethod } from '../types/billPayment';
import type { Bill } from '../types/bill';
import type { EditableAttachment, PendingAttachment } from '../types/attachmentsForm';
import {
  getStagedAttachmentIds,
  hasPendingUploadFailed,
  hasPendingUploadInProgress,
  stagedAttachmentToPending,
} from '../types/attachmentsForm';
import { formatCurrency } from '../utils/formatCurrency';
import { toast, toastApiError } from '../utils/toast';
import { todayLocalIso } from '../utils/localDate';
import { canManageBills } from '../utils/purchaseTransactionPermissions';
import { usePayBillsHubOptional } from './PayBillsHubContext';

const PAYMENT_METHODS: { value: BillPaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online' },
];

interface OpenBillLine {
  bill_id: number;
  bill_number: string;
  vendor_bill_number: string | null;
  txn_date: string;
  total: number;
  balance: number;
  selected: boolean;
  paymentAmount: string;
}

const MIN_ALLOCATION = 0.01;

const billDisplayLabel = (line: Pick<OpenBillLine, 'bill_number' | 'vendor_bill_number'>) =>
  line.vendor_bill_number ?? line.bill_number;

const supplierToOption = (supplier: Supplier): SearchableSelectOption => ({
  value: supplier.id,
  label: supplier.display_name,
  description: [supplier.email, supplier.phone].filter(Boolean).join(' · ') || undefined,
});

const today = todayLocalIso;

const parsePayment = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const canRecordBillPayment = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const payableBillsToLines = (bills: Bill[]): OpenBillLine[] =>
  bills.map((b) => ({
    bill_id: b.id,
    bill_number: b.bill_number,
    vendor_bill_number: b.vendor_bill_number,
    txn_date: b.txn_date.slice(0, 10),
    total: parseFloat(b.total_amount),
    balance: parseFloat(b.balance),
    selected: false,
    paymentAmount: '',
  }));

const autoApplyFifo = (lines: OpenBillLine[], amount: number): OpenBillLine[] => {
  let remaining = amount;
  return lines.map((line) => {
    if (remaining <= 0) {
      return { ...line, selected: false, paymentAmount: '' };
    }
    const applied = Math.min(remaining, line.balance);
    remaining -= applied;
    return {
      ...line,
      selected: applied > 0,
      paymentAmount: applied > 0 ? applied.toFixed(2) : '',
    };
  });
};

const formatPaymentSuccessMessage = (payment: BillPayment): string => {
  const total = formatCurrency(parseFloat(payment.total_amount));
  const unapplied = parseFloat(payment.unapplied_amount);
  const allocationCount = payment.allocations.length;

  if (allocationCount === 0) {
    return `Prepayment ${payment.payment_number} of ${total} recorded as unapplied vendor credit.`;
  }

  if (unapplied > MIN_ALLOCATION) {
    return `Payment ${payment.payment_number} of ${total} recorded (${formatCurrency(parseFloat(payment.applied_amount))} applied, ${formatCurrency(unapplied)} unapplied prepayment).`;
  }

  return `Payment ${payment.payment_number} of ${total} applied to ${allocationCount} bill(s).`;
};

const formatCreditApplySuccessMessage = (payment: BillPayment): string => {
  const unapplied = parseFloat(payment.unapplied_amount);
  if (unapplied > MIN_ALLOCATION) {
    return `Prepayment from ${payment.payment_number} applied to bills. ${formatCurrency(unapplied)} remains unapplied.`;
  }
  return `Prepayment from ${payment.payment_number} fully applied to bills.`;
};

type PaymentMode = 'new' | 'credit';

export type PayBillsProps = {
  embedded?: boolean;
  initialVendorId?: number;
  initialBillId?: number;
  onClose?: () => void;
  onPaymentSaved?: (payment: BillPayment) => void;
};

const PayBills = ({
  embedded = false,
  initialVendorId,
  initialBillId,
  onClose,
  onPaymentSaved,
}: PayBillsProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const payBillsHub = usePayBillsHubOptional();
  const routeState = location.state as { vendorId?: number; billId?: number } | null;
  const resolvedInitialVendorId = embedded
    ? (initialVendorId ?? 0)
    : (routeState?.vendorId ?? 0);
  const resolvedInitialBillId = embedded
    ? (initialBillId ?? null)
    : (routeState?.billId ?? null);
  const preselectedBillIdRef = useRef<number | null>(resolvedInitialBillId);
  const loadGenRef = useRef(0);
  const supplierIdRef = useRef(resolvedInitialVendorId);
  const supplierLookupRef = useRef<Map<number, Supplier>>(new Map());
  const selectedVendorOptionRef = useRef<SearchableSelectOption | null>(null);
  const [vendorOptions, setVendorOptions] = useState<SearchableSelectOption[]>([]);
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
  const [selectedVendorOption, setSelectedVendorOption] = useState<SearchableSelectOption | null>(
    null
  );
  const [supplierId, setSupplierId] = useState(resolvedInitialVendorId);
  const [supplierName, setSupplierName] = useState('');
  const [lines, setLines] = useState<OpenBillLine[]>([]);
  const [totalDue, setTotalDue] = useState(0);
  const [unappliedTotal, setUnappliedTotal] = useState(0);
  const [unappliedPayments, setUnappliedPayments] = useState<BillPayment[]>([]);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('new');
  const [selectedCreditPaymentId, setSelectedCreditPaymentId] = useState(0);

  const [loadingBills, setLoadingBills] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const [paymentDate, setPaymentDate] = useState(today());
  const [paymentMethod, setPaymentMethod] = useState<BillPaymentMethod | ''>('');
  const [referenceNo, setReferenceNo] = useState('');
  const [memo, setMemo] = useState('');
  const [amountToPay, setAmountToPay] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [existingAttachments] = useState<EditableAttachment[]>([]);
  const [uploadSessionId] = useState(() =>
    getOrCreateUploadSessionId(BILL_PAYMENT_UPLOAD_SESSION_KEY)
  );

  useEffect(() => {
    if (embedded && !canRecordBillPayment()) {
      onClose?.();
    }
  }, [embedded, onClose]);

  const loadStagedAttachments = useCallback(async () => {
    const staged = await listStagedAttachments({
      upload_session_id: uploadSessionId,
      intended_attachable_type: 'bill_payment',
    }).catch((err) => {
      console.error('[PayBills] Failed to load staged attachments', err);
      return [];
    });
    setPendingAttachments(staged.map(stagedAttachmentToPending));
  }, [uploadSessionId]);

  useEffect(() => {
    void loadStagedAttachments();
  }, [loadStagedAttachments]);

  const buildAttachmentFields = () => {
    const stagedIds = getStagedAttachmentIds(pendingAttachments);
    return stagedIds.length > 0 ? { staged_attachment_ids: stagedIds } : {};
  };

  const finishPaymentSaved = (payment: BillPayment, successMessage: string) => {
    if (embedded) {
      toast.success(successMessage);
      onPaymentSaved?.(payment);
      return;
    }
    navigate('/purchase-transactions', {
      state: { successMessage, vendorId: supplierId },
    });
  };

  const loadOpenBills = async (id: number) => {
    const gen = ++loadGenRef.current;
    setLoadingBills(true);
    setError(null);
    try {
      const [payableBills, unappliedPaymentsList] = await Promise.all([
        getVendorPayableBills(id),
        getBillPayments({ vendor_id: id, unapplied: true }),
      ]);
      if (gen !== loadGenRef.current) return;
      const activeUnappliedPayments = unappliedPaymentsList.filter(
        (payment) => (payment.status ?? 'POSTED') !== 'VOIDED'
      );

      let supplier =
        supplierLookupRef.current.get(id) ??
        (await getSupplierById(id).catch(() => null));
      if (supplier) {
        supplierLookupRef.current.set(id, supplier);
      }

      let openLines = payableBillsToLines(payableBills);
      const preId = preselectedBillIdRef.current;
      if (preId) {
        preselectedBillIdRef.current = null;
        openLines = openLines.map((line) =>
          line.bill_id === preId
            ? { ...line, selected: true, paymentAmount: line.balance.toFixed(2) }
            : line
        );
      }

      const unapplied = activeUnappliedPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.unapplied_amount),
        0
      );

      setSupplierName(
        supplier?.display_name ?? selectedVendorOptionRef.current?.label ?? ''
      );
      setTotalDue(openLines.reduce((sum, line) => sum + line.balance, 0));
      setUnappliedTotal(unapplied);
      setUnappliedPayments(activeUnappliedPayments);
      setPaymentMode('new');
      setSelectedCreditPaymentId(0);
      setLines(openLines);
      setAmountToPay('');
      setReferenceNo('');
      setMemo('');
      setPaymentDate(today());
      setPaymentMethod('');
    } catch (err) {
      if (gen !== loadGenRef.current) return;
      setLines([]);
      setTotalDue(0);
      setUnappliedTotal(0);
      setUnappliedPayments([]);
      setPaymentMode('new');
      setSelectedCreditPaymentId(0);
      setError(err instanceof Error ? err.message : 'Failed to load open bills');
    } finally {
      if (gen === loadGenRef.current) setLoadingBills(false);
    }
  };

  useEffect(() => {
    supplierIdRef.current = supplierId;
  }, [supplierId]);

  useEffect(() => {
    if (!payBillsHub) return;
    payBillsHub.registerRefetch(() => {
      const id = supplierIdRef.current;
      if (id > 0) {
        void loadOpenBills(id);
      }
    });
  }, [payBillsHub, supplierId]);

  useEffect(() => {
    selectedVendorOptionRef.current = selectedVendorOption;
  }, [selectedVendorOption]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setVendorSearchLoading(true);
      try {
        const result = await getSuppliers({
          search: vendorSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const supplier of result.suppliers) {
          supplierLookupRef.current.set(supplier.id, supplier);
        }
        const options = result.suppliers.map(supplierToOption);
        const selected = selectedVendorOptionRef.current;
        setVendorOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setVendorSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearchInput]);

  useEffect(() => {
    if (!resolvedInitialVendorId) return;

    const id = resolvedInitialVendorId;
    void (async () => {
      try {
        const supplier = await getSupplierById(id);
        supplierLookupRef.current.set(id, supplier);
        const option = supplierToOption(supplier);
        setSelectedVendorOption(option);
        selectedVendorOptionRef.current = option;
        setVendorOptions([option]);
        setSupplierName(supplier.display_name);
      } catch {
        // loadOpenBills will still run
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!embedded && routeState?.vendorId) {
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!supplierId) {
      setLines([]);
      setTotalDue(0);
      setUnappliedTotal(0);
      setUnappliedPayments([]);
      setPaymentMode('new');
      setSelectedCreditPaymentId(0);
      setSupplierName('');
      setAmountToPay('');
      return;
    }
    loadOpenBills(supplierId);
  }, [supplierId]);

  const totalApplied = useMemo(
    () => lines.reduce((sum, line) => sum + parsePayment(line.paymentAmount), 0),
    [lines]
  );

  const enteredAmount = useMemo(() => parsePayment(amountToPay), [amountToPay]);

  const selectedCreditPayment = useMemo(
    () => unappliedPayments.find((payment) => payment.id === selectedCreditPaymentId) ?? null,
    [unappliedPayments, selectedCreditPaymentId]
  );

  const creditAvailable = useMemo(
    () => (selectedCreditPayment ? parseFloat(selectedCreditPayment.unapplied_amount) : 0),
    [selectedCreditPayment]
  );

  const unappliedPreview = useMemo(
    () => Math.max(0, enteredAmount - totalApplied),
    [enteredAmount, totalApplied]
  );

  const canRecordAppliedPayment = totalApplied >= MIN_ALLOCATION;
  const canRecordUnappliedPayment =
    paymentMode === 'new' && !canRecordAppliedPayment && enteredAmount >= MIN_ALLOCATION;
  const canApplyCredit =
    paymentMode === 'credit' && selectedCreditPaymentId > 0 && totalApplied >= MIN_ALLOCATION;

  const resetLineAllocations = () => {
    setLines((prev) => prev.map((line) => ({ ...line, selected: false, paymentAmount: '' })));
    setAmountToPay('');
    setError(null);
  };

  const switchPaymentMode = (mode: PaymentMode) => {
    setPaymentMode(mode);
    setSelectedCreditPaymentId(0);
    resetLineAllocations();
  };

  const buildAllocations = () =>
    lines
      .map((line) => ({
        bill_id: line.bill_id,
        amount_applied: parsePayment(line.paymentAmount),
      }))
      .filter((row) => row.amount_applied >= MIN_ALLOCATION);

  const selectedCount = useMemo(
    () => lines.filter((line) => line.selected && parsePayment(line.paymentAmount) > 0).length,
    [lines]
  );

  const allSelected =
    lines.length > 0 &&
    lines.every((line) => line.selected && parsePayment(line.paymentAmount) === line.balance);

  const handleVendorChange = async (value: number | string) => {
    const id = Number(value);
    setSupplierId(id);

    if (!id) {
      setSelectedVendorOption(null);
      selectedVendorOptionRef.current = null;
      setSupplierName('');
      return;
    }

    let supplier = supplierLookupRef.current.get(id);
    if (!supplier) {
      try {
        supplier = await getSupplierById(id);
        supplierLookupRef.current.set(id, supplier);
      } catch {
        return;
      }
    }

    const option = supplierToOption(supplier);
    setSelectedVendorOption(option);
    selectedVendorOptionRef.current = option;
    setVendorOptions((prev) =>
      prev.some((opt) => String(opt.value) === String(id)) ? prev : [option, ...prev]
    );
    setSupplierName(supplier.display_name);
  };

  const updateLine = (billId: number, patch: Partial<OpenBillLine>) => {
    setLines((prev) => prev.map((line) => (line.bill_id === billId ? { ...line, ...patch } : line)));
  };

  const maxAmountForLine = (line: OpenBillLine) => {
    const currentLineAmount = parsePayment(line.paymentAmount);
    const otherApplied = totalApplied - currentLineAmount;
    if (paymentMode === 'credit') {
      return Math.min(line.balance, Math.max(0, creditAvailable - otherApplied));
    }
    return line.balance;
  };

  const toggleLine = (billId: number, checked: boolean) => {
    const line = lines.find((row) => row.bill_id === billId);
    if (!line) return;

    const maxForLine = maxAmountForLine(line);
    updateLine(billId, {
      selected: checked && maxForLine >= MIN_ALLOCATION,
      paymentAmount: checked && maxForLine >= MIN_ALLOCATION ? maxForLine.toFixed(2) : '',
    });
  };

  const handlePaymentChange = (billId: number, value: string) => {
    const line = lines.find((row) => row.bill_id === billId);
    if (!line) return;

    const currentLineAmount = parsePayment(line.paymentAmount);
    const otherApplied = totalApplied - currentLineAmount;
    const maxForLine =
      paymentMode === 'credit'
        ? Math.min(line.balance, Math.max(0, creditAvailable - otherApplied))
        : line.balance;

    const amount = parsePayment(value);
    const capped = amount > maxForLine ? maxForLine.toFixed(2) : value;

    updateLine(billId, {
      paymentAmount: capped,
      selected: parsePayment(capped) >= MIN_ALLOCATION,
    });
  };

  const openAmendBill = (billId: number) => {
    if (!canManageBills()) return;
    const returnTo = `${location.pathname}${location.search}`;
    navigate(`bill/${billId}`, {
      preventScrollReset: true,
      state: { returnTo },
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && paymentMode === 'credit') {
      if (!selectedCreditPaymentId || creditAvailable < MIN_ALLOCATION) {
        setError('Select an unapplied payment with available prepayment.');
        return;
      }
      setError(null);
      setLines((prev) => autoApplyFifo(prev, creditAvailable));
      return;
    }

    setLines((prev) =>
      prev.map((line) => ({
        ...line,
        selected: checked,
        paymentAmount: checked ? line.balance.toFixed(2) : '',
      }))
    );
  };

  const handleClearPayments = () => {
    resetLineAllocations();
  };

  const handleAutoApply = () => {
    if (paymentMode === 'credit') {
      if (!selectedCreditPaymentId || creditAvailable < MIN_ALLOCATION) {
        setError('Select an unapplied payment with available prepayment.');
        return;
      }
      setError(null);
      setLines((prev) => autoApplyFifo(prev, creditAvailable));
      return;
    }

    const amount = parsePayment(amountToPay);
    if (amount < MIN_ALLOCATION) {
      setError('Enter an amount to pay before auto-applying.');
      return;
    }
    if (amount > totalDue + MIN_ALLOCATION) {
      setError(`Amount cannot exceed total open balance of ${formatCurrency(totalDue)}.`);
      return;
    }
    setError(null);
    setLines((prev) => autoApplyFifo(prev, amount));
  };

  const handlePayAll = () => {
    if (paymentMode === 'credit') {
      if (!selectedCreditPaymentId || creditAvailable < MIN_ALLOCATION) {
        setError('Select an unapplied payment with available prepayment.');
        return;
      }
      setError(null);
      setLines((prev) => autoApplyFifo(prev, Math.min(totalDue, creditAvailable)));
      return;
    }

    handleSelectAll(true);
    setAmountToPay(totalDue.toFixed(2));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await submitPayment(false);
  };

  const handleSubmitUnapplied = async () => {
    await submitPayment(true);
  };

  const handleSubmitCredit = async () => {
    await submitCreditAllocation();
  };

  const validateLineAllocations = (allocations: { bill_id: number; amount_applied: number }[]) => {
    if (allocations.length === 0) {
      setError('Select at least one bill and enter a payment amount.');
      return false;
    }

    for (const line of lines) {
      const amount = parsePayment(line.paymentAmount);
      if (amount > line.balance + MIN_ALLOCATION) {
        setError(
          `Payment for ${billDisplayLabel(line)} cannot exceed ${formatCurrency(line.balance)}.`
        );
        return false;
      }
    }

    return true;
  };

  const submitCreditAllocation = async () => {
    if (!supplierId || !selectedCreditPaymentId || !canRecordBillPayment()) return;

    setError(null);
    setFieldErrors({});

    const allocations = buildAllocations();
    if (!validateLineAllocations(allocations)) return;

    const appliedTotal = allocations.reduce((sum, row) => sum + row.amount_applied, 0);
    if (appliedTotal > creditAvailable + MIN_ALLOCATION) {
      setError(
        `Total applied cannot exceed available prepayment of ${formatCurrency(creditAvailable)} on ${selectedCreditPayment?.payment_number ?? 'this payment'}.`
      );
      return;
    }

    setSubmitting(true);
    try {
      const payment = await applyBillPaymentAllocations(selectedCreditPaymentId, { allocations });
      finishPaymentSaved(payment, formatCreditApplySuccessMessage(payment));
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to apply unapplied prepayment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitPayment = async (unappliedOnly: boolean) => {
    if (!supplierId || !canRecordBillPayment()) return;

    setError(null);
    setFieldErrors({});

    const allocations = unappliedOnly ? [] : buildAllocations();

    if (!unappliedOnly && !validateLineAllocations(allocations)) return;

    const appliedTotal = allocations.reduce((sum, row) => sum + row.amount_applied, 0);
    const enteredTotal = parsePayment(amountToPay);
    const totalAmount = unappliedOnly
      ? enteredTotal
      : enteredTotal > appliedTotal
        ? enteredTotal
        : appliedTotal;

    if (totalAmount < MIN_ALLOCATION) {
      setError(
        unappliedOnly
          ? 'Enter an amount to pay of at least 0.01 to record a vendor prepayment.'
          : 'Total payment amount must be at least 0.01.'
      );
      return;
    }

    if (!unappliedOnly && appliedTotal > totalAmount + MIN_ALLOCATION) {
      setError('Total applied cannot exceed the payment amount.');
      return;
    }

    if (!paymentMethod) {
      setError('Select a payment method.');
      return;
    }

    if (paymentMethod === 'CHEQUE' && !referenceNo.trim()) {
      setError('Reference number is required for cheque payments.');
      return;
    }

    if (hasPendingUploadInProgress(pendingAttachments)) {
      setError('Please wait for file uploads to finish before saving.');
      return;
    }
    if (hasPendingUploadFailed(pendingAttachments)) {
      setError('Remove or retry failed file uploads before saving.');
      return;
    }

    const hasStagedNoteWork = pendingAttachments.some(
      (row) => row.uploadStatus === 'staged' && row.note !== (row.attachment?.note ?? '')
    );

    setSubmitting(true);
    try {
      if (hasStagedNoteWork) {
        const noteFailures = await syncStagedAttachmentNotes(pendingAttachments);
        if (noteFailures.length > 0) {
          setError(`Could not update attachment notes: ${noteFailures.join(' ')}`);
          return;
        }
      }

      const payment = await createBillPayment({
        vendor_id: supplierId,
        txn_date: paymentDate,
        total_amount: totalAmount,
        payment_method: paymentMethod,
        ref_number: referenceNo.trim() || null,
        memo: memo.trim() || null,
        allocations,
        ...buildAttachmentFields(),
      });

      finishPaymentSaved(payment, formatPaymentSuccessMessage(payment));
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to record payment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canRecordBillPayment()) {
    if (embedded) {
      return (
        <div className="flex flex-col h-full min-h-0 bg-gray-100 p-6 items-center justify-center">
          <p className="text-gray-600 text-sm">
            Only administrators and accountants can record bill payments.
          </p>
        </div>
      );
    }

    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center min-h-0">
          <p className="text-gray-600 text-sm">
            Only administrators and accountants can record bill payments.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div
      className={
        embedded ? 'flex flex-col h-full min-h-0 bg-gray-100' : 'flex h-screen bg-gray-100'
      }
    >
      {!embedded && <Sidebar />}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className={`flex-1 overflow-auto ${embedded ? 'p-4' : 'p-4 md:p-6'}`}>
        <div className="w-full max-w-full">
          <div className={embedded ? 'mb-4' : 'mb-6'}>
            <h1 className={`font-semibold text-gray-900 ${embedded ? 'text-lg' : 'text-xl md:text-2xl'}`}>
              Pay Bills
            </h1>
            {!embedded && (
              <p className="mt-1 text-sm text-gray-500">
                Record new payments, save vendor prepayment, or apply existing unapplied prepayment to
                bills
              </p>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form id="pay-bills-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-white rounded-lg shadow p-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Vendor <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    options={vendorOptions}
                    value={supplierId}
                    onChange={(value) => void handleVendorChange(value)}
                    placeholder="Select a vendor"
                    searchPlaceholder="Search vendors..."
                    emptyMessage="No vendors found"
                    onSearchChange={setVendorSearchInput}
                    isLoading={vendorSearchLoading}
                  />
                </div>

                {supplierId > 0 && (
                  <div className="flex flex-col justify-end gap-2 w-full">
                    <div className="grid grid-cols-2 gap-3 w-full">
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Open balance</p>
                        <p className="text-lg font-semibold text-red-600">
                          {loadingBills ? '—' : formatCurrency(totalDue)}
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Unapplied prepayment
                        </p>
                        <p className="text-lg font-semibold text-amber-700">
                          {loadingBills ? '—' : formatCurrency(unappliedTotal)}
                        </p>
                      </div>
                    </div>
                    {supplierName && <p className="text-sm text-gray-600">{supplierName}</p>}
                  </div>
                )}
              </div>
            </div>

            {supplierId > 0 && (
              <>
                {unappliedPayments.length > 0 && (
                  <div className="bg-white rounded-lg shadow p-5">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                      Payment action
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => switchPaymentMode('new')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                          paymentMode === 'new'
                            ? 'bg-slate-800 text-white border-slate-800'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        Pay new bill payment
                      </button>
                      <button
                        type="button"
                        onClick={() => switchPaymentMode('credit')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                          paymentMode === 'credit'
                            ? 'bg-amber-700 text-white border-amber-700'
                            : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-50'
                        }`}
                      >
                        Apply unapplied prepayment
                      </button>
                    </div>
                    {paymentMode === 'credit' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Unapplied payment <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedCreditPaymentId}
                          onChange={(e) => {
                            setSelectedCreditPaymentId(Number(e.target.value));
                            resetLineAllocations();
                          }}
                          className="w-full max-w-xl border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value={0}>Select a payment</option>
                          {unappliedPayments.map((payment) => (
                            <option key={payment.id} value={payment.id}>
                              {payment.payment_number} —{' '}
                              {formatCurrency(parseFloat(payment.unapplied_amount))} available (
                              {payment.txn_date.slice(0, 10)})
                            </option>
                          ))}
                        </select>
                        {selectedCreditPayment && (
                          <p className="mt-2 text-xs text-gray-500">
                            {formatCurrency(creditAvailable)} available from{' '}
                            {selectedCreditPayment.payment_number}. No new cash or journal entry —
                            prepayment is applied to bills only.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {paymentMode === 'new' && (
                  <>
                  <div className="bg-white rounded-lg shadow p-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                          Payment details
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Payment date
                            </label>
                            <input
                              type="date"
                              value={paymentDate}
                              onChange={(e) => setPaymentDate(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                            {fieldErrors.txn_date?.[0] && (
                              <p className="mt-1 text-xs text-red-600">{fieldErrors.txn_date[0]}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Payment method <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={paymentMethod}
                              onChange={(e) => setPaymentMethod(e.target.value as BillPaymentMethod | '')}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                            >
                              <option value="">Select payment method</option>
                              {PAYMENT_METHODS.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </select>
                            {fieldErrors.payment_method?.[0] && (
                              <p className="mt-1 text-xs text-red-600">{fieldErrors.payment_method[0]}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Reference no.
                              {paymentMethod === 'CHEQUE' && <span className="text-red-500"> *</span>}
                            </label>
                            <input
                              type="text"
                              maxLength={100}
                              value={referenceNo}
                              onChange={(e) => setReferenceNo(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder={paymentMethod === 'CHEQUE' ? 'Cheque number' : 'Bank / cheque ref'}
                              required={paymentMethod === 'CHEQUE'}
                            />
                            {fieldErrors.ref_number?.[0] && (
                              <p className="mt-1 text-xs text-red-600">{fieldErrors.ref_number[0]}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                            <input
                              type="text"
                              value={memo}
                              onChange={(e) => setMemo(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder="Optional"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Payment number (BPAY-0001, …) is assigned automatically on save.
                        </p>
                      </div>

                      <AttachmentsSection
                        title="Attachments"
                        description="Remittance advice, wire confirmations, and other supporting documents. Files upload immediately and link when you save."
                        showIncludeOnSend={false}
                        parentIsDraft={false}
                        stageOnPick
                        intendedAttachableType="bill_payment"
                        uploadSessionId={uploadSessionId}
                        pending={pendingAttachments}
                        onPendingChange={setPendingAttachments}
                        existing={existingAttachments}
                        onExistingChange={() => {}}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                  </>
                )}

                <div className="bg-white rounded-lg shadow">
                  <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                        Outstanding bills
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {paymentMode === 'credit'
                          ? 'Allocate existing unapplied prepayment to received bills'
                          : 'Received bills with open or partial balance'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {paymentMode === 'new' ? (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600 whitespace-nowrap">Amount to pay</label>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={amountToPay}
                            onChange={(e) => setAmountToPay(e.target.value)}
                            className="w-32 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600 whitespace-nowrap">Prepayment available</span>
                          <span className="font-semibold text-amber-700">
                            {selectedCreditPayment ? formatCurrency(creditAvailable) : '—'}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleAutoApply}
                        className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                      >
                        Auto apply
                      </button>
                      <button
                        type="button"
                        onClick={handlePayAll}
                        className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg"
                      >
                        Pay all
                      </button>
                      <button
                        type="button"
                        onClick={handleClearPayments}
                        className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {loadingBills ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading open bills...
                    </div>
                  ) : lines.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-12 px-4">
                      {paymentMode === 'credit'
                        ? 'No open bills for this vendor. Receive a bill before applying prepayment.'
                        : 'No open bills for this vendor. Enter an amount to pay above and use Record as prepayment to save vendor credit.'}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 w-10">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-slate-800"
                                aria-label="Select all bills"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Bill #
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Total
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                              Balance
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-40">
                              Payment
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {lines.map((line) => {
                            const payment = parsePayment(line.paymentAmount);
                            const overBalance = payment > line.balance + MIN_ALLOCATION;
                            const overCredit =
                              paymentMode === 'credit' &&
                              totalApplied > creditAvailable + MIN_ALLOCATION;

                            return (
                              <tr
                                key={line.bill_id}
                                className={line.selected ? 'bg-slate-50/60' : undefined}
                              >
                                <td className="px-4 py-2">
                                  <input
                                    type="checkbox"
                                    checked={line.selected}
                                    onChange={(e) => toggleLine(line.bill_id, e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-slate-800"
                                    aria-label={`Select bill ${billDisplayLabel(line)}`}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                  {line.txn_date}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {canManageBills() ? (
                                    <button
                                      type="button"
                                      onClick={() => openAmendBill(line.bill_id)}
                                      className="text-left text-slate-800 hover:text-slate-600 hover:underline"
                                      title="Amend bill"
                                    >
                                      {line.vendor_bill_number ? (
                                        <>
                                          <div className="font-medium">{line.vendor_bill_number}</div>
                                          <div className="text-xs font-mono text-gray-500 mt-0.5">
                                            {line.bill_number}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="font-medium font-mono">{line.bill_number}</div>
                                      )}
                                    </button>
                                  ) : line.vendor_bill_number ? (
                                    <>
                                      <div className="font-medium text-gray-900">
                                        {line.vendor_bill_number}
                                      </div>
                                      <div className="text-xs font-mono text-gray-500 mt-0.5">
                                        {line.bill_number}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="font-medium text-gray-900 font-mono">
                                      {line.bill_number}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                                  {formatCurrency(line.total)}
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-red-600 text-right whitespace-nowrap">
                                  {formatCurrency(line.balance)}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      max={line.balance}
                                      value={line.paymentAmount}
                                      onChange={(e) => handlePaymentChange(line.bill_id, e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      className={`w-28 border rounded-lg px-2.5 py-1.5 text-sm text-right ${
                                        overBalance || overCredit
                                          ? 'border-red-400 bg-red-50'
                                          : 'border-gray-300'
                                      }`}
                                      placeholder="0.00"
                                    />
                                    {line.balance > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => toggleLine(line.bill_id, true)}
                                        className="text-xs text-slate-600 hover:text-slate-900 whitespace-nowrap px-1"
                                        title="Pay full balance"
                                      >
                                        Full
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {supplierId > 0 && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 md:px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              {lines.length > 0 && (
                <>
                  <span className="text-gray-600">
                    Bills selected:{' '}
                    <span className="font-medium text-gray-900">{selectedCount}</span>
                  </span>
                  <span className="text-gray-600">
                    Open balance:{' '}
                    <span className="font-medium text-red-600">{formatCurrency(totalDue)}</span>
                  </span>
                </>
              )}
              {paymentMode === 'credit' && selectedCreditPayment && (
                <span className="text-gray-600">
                  Prepayment remaining:{' '}
                  <span className="font-medium text-amber-700">
                    {formatCurrency(Math.max(0, creditAvailable - totalApplied))}
                  </span>
                </span>
              )}
              {paymentMode === 'new' && unappliedPreview > MIN_ALLOCATION && (
                <span className="text-gray-600">
                  Unapplied prepayment:{' '}
                  <span className="font-medium text-amber-700">{formatCurrency(unappliedPreview)}</span>
                </span>
              )}
              {lines.length > 0 && (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-slate-600" />
                  <span className="text-gray-600">Amount to apply</span>
                  <span className="text-lg font-semibold text-slate-900">
                    {formatCurrency(totalApplied)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {embedded && onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
              {paymentMode === 'credit' ? (
                <button
                  type="button"
                  onClick={handleSubmitCredit}
                  disabled={submitting || !canApplyCredit}
                  className="flex items-center px-6 py-2.5 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Applying...
                    </>
                  ) : (
                    'Apply prepayment to bills'
                  )}
                </button>
              ) : canRecordAppliedPayment ? (
                <button
                  type="submit"
                  form="pay-bills-form"
                  disabled={submitting}
                  className="flex items-center px-6 py-2.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Record payment'
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmitUnapplied}
                  disabled={submitting || !canRecordUnappliedPayment}
                  className="flex items-center px-6 py-2.5 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Record as prepayment'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PayBills;
